import React, { useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/**
 * LegacyProjectRedirect - Handles old project URLs and redirects to new route structure
 * Supports redirecting from /project-manager/edit/:pageId?tab=... to /project/:id/...
 */
const LegacyProjectRedirect: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!pageId) {
      navigate('/project-manager', { replace: true });
      return;
    }
    
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const openPage = params.get('openPage');
    const openTextMagic = params.get('openTextMagic');
    
    // Determine the new route based on the old parameters
    let newRoute = `/project/${pageId}`;
    
    if (openPage) {
      // Direct page navigation: ?openPage=123 -> /project/:id/page/123
      newRoute = `/project/${pageId}/page/${openPage}`;
    } else if (tab) {
      // Tab-based navigation: ?tab=settings -> /project/:id/settings
      if (tab.startsWith('page-')) {
        const extractedPageId = tab.replace('page-', '');
        newRoute = `/project/${pageId}/page/${extractedPageId}`;
      } else if (tab === 'settings') {
        newRoute = `/project/${pageId}/settings`;
      } else if (tab === 'images') {
        newRoute = `/project/${pageId}/images`;
      } else if (tab === 'resources') {
        newRoute = `/project/${pageId}/resources`;
      } else if (tab === 'pages') {
        newRoute = `/project/${pageId}/pages`;
      } else {
        // Default to tasks for unknown tabs
        newRoute = `/project/${pageId}/tasks`;
      }
    } else {
      // No specific tab, default to tasks
      newRoute = `/project/${pageId}/tasks`;
    }
    
    console.log('[LegacyProjectRedirect] Redirecting from', location.pathname + location.search, 'to', newRoute);
    navigate(newRoute, { replace: true });
  }, [pageId, location, navigate]);
  
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-500" />
        <p className="text-gray-600 dark:text-gray-300">Redirecting to new project interface...</p>
      </div>
    </div>
  );
};

export default LegacyProjectRedirect; 